import { checkPartnerCode } from "@kovernow/flows/auth";
import type { RecoveryAction } from "@kovernow/flows/recovery";
import { Button } from "@kovernow/react-ui/components/button";
import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router";
import { useServices, useSession } from "../core/app-context";
import { partnerStorage } from "../core/partner-storage";
import { useT } from "../core/use-t";
import { Page } from "../components/Page";
import { Recovery } from "../components/Recovery";

/** A lookup that couldn't run (network, 5xx): retryable, never "unknown". */
const UNCHECKED: RecoveryAction = {
  errorCode: "partner_unchecked",
  messageKey: "errors.partner_unchecked",
  action: "retry",
  kind: "recoverable",
};

/**
 * `/p/<partner>` (W-1): check the code once, keep a valid one for this tab and
 * go to sign-up. Unknown/inactive codes are dropped (sign-up shows an info
 * banner). A lookup that fails stays here with Try again, or Continue, which
 * goes on without the code (the same "not recognised" banner).
 * A signed-in visit goes home and the code is dropped (no back-fill on web).
 */
export function PartnerCapture() {
  const { partner = "" } = useParams();
  const { authApi } = useServices();
  const { authenticated } = useSession();
  const navigate = useNavigate();
  const t = useT();
  const [unchecked, setUnchecked] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (authenticated) {
      // Signed-in visit: no back-fill on web, so any stored code is dropped.
      partnerStorage.clear();
      return;
    }
    let cancelled = false;
    void checkPartnerCode(authApi, partner).then((res) => {
      if (cancelled) return;
      if (res.status === "unchecked") {
        setUnchecked(true);
        return;
      }
      if (res.status === "valid") partnerStorage.set(res.code);
      else partnerStorage.clear();
      navigate(res.status === "valid" ? "/sign-up" : "/sign-up?partner=unknown", { replace: true });
    });
    return () => {
      cancelled = true;
    };
  }, [authenticated, authApi, partner, navigate, attempt]);

  if (authenticated) return <Navigate to="/" replace />;
  return (
    <Page titleKey="auth.partner.title" flowKey="flows.signUp" className="mx-auto w-full max-w-md px-4 py-8">
      {unchecked ? (
        <div className="flex flex-col gap-6">
          <Recovery
            action={UNCHECKED}
            onRetry={() => {
              setUnchecked(false);
              setAttempt((n) => n + 1);
            }}
          />
          <Button
            type="button"
            variant="link"
            className="self-start px-0"
            onClick={() => {
              partnerStorage.clear();
              navigate("/sign-up?partner=unknown", { replace: true });
            }}
          >
            {t("auth.partner.continue")}
          </Button>
        </div>
      ) : (
        <p className="text-muted-foreground" role="status">
          {t("auth.partner.checking")}
        </p>
      )}
    </Page>
  );
}
